/**
 * 通用资源「新建」WallpaperCo 构建工厂。
 *
 * 职责：
 * - 批量上传落库（resourceSave）与手动新建共用同一 API 契约；
 * - 在写入 addGenericResource 前补齐数组/集合字段，避免 DB 存 NULL；
 * - 详情页对 androidNeglectVersion / iosNeglectVersion 直接 .join()，
 *   因此这些字段必须是数组，不能省略或传 null。
 *
 * 架构位置：genericResourceManage 边界适配（非 COLA 层；前端领域 → WallpaperCo 契约）。
 *
 * 边界：
 * - 仅服务「新建」，不含 id，不用于编辑；
 * - 对外接受 resourceName / assetType，经 toWallpaperCo 映射为 wallpaperName / wallpaperType；
 * - 不处理表单 UI 字符串转换（category 逗号串、exta JSON parse 等仍由页面完成）。
 */

import { toWallpaperCo } from './genericResourceAdapter'

const DEFAULT_COUNTRY_LIST = Object.freeze({
  isExclude: true,
  countries: []
})

/**
 * @param {unknown} value
 * @param {string} fieldName
 * @returns {string}
 */
function requireNonEmptyString(value, fieldName) {
  const text = String(value == null ? '' : value).trim()
  if (!text) {
    throw new Error(`${fieldName} 不能为空`)
  }
  return text
}

/**
 * 版本忽略列表：手动创建提交前已是数组；批量路径缺省为 []。
 *
 * @param {unknown} value
 * @returns {Array<string|number>}
 */
function normalizeNeglectVersion(value) {
  if (Array.isArray(value)) {
    return value
  }
  if (value == null || value === '') {
    return []
  }
  // 版本 Code 有时以 number 入参（如 30100），不能静默丢成 []
  if (typeof value === 'number' && Number.isFinite(value)) {
    return [value]
  }
  if (typeof value === 'string') {
    const parts = value.split(',').map((item) => item.trim())
    if (parts.length === 1 && parts[0] === '') {
      return []
    }
    return parts
  }
  return []
}

/**
 * @param {unknown} value
 * @returns {Array<*>}
 */
function normalizeJsonArray(value) {
  return Array.isArray(value) ? value : []
}

/**
 * tagKeySet 在后端为 Set，JSON 序列化用数组；禁止 null。
 *
 * @param {unknown} value
 * @returns {Array<string>}
 */
function normalizeTagKeySet(value) {
  if (value == null) {
    return []
  }
  if (Array.isArray(value)) {
    return value
  }
  if (value instanceof Set) {
    return Array.from(value)
  }
  return []
}

/**
 * categoryIdList 在 add 侧按逗号字符串 split；手动创建已是字符串，批量传 ''。
 *
 * @param {unknown} value
 * @returns {string}
 */
function normalizeCategoryIdList(value) {
  if (value == null) {
    return ''
  }
  if (Array.isArray(value)) {
    return value.join(',')
  }
  return String(value)
}

/**
 * @param {unknown} value
 * @returns {{ isExclude: boolean, countries: Array<*> }}
 */
function normalizeCountryList(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      isExclude: value.isExclude !== false,
      countries: normalizeJsonArray(value.countries)
    }
  }
  return {
    isExclude: DEFAULT_COUNTRY_LIST.isExclude,
    countries: [...DEFAULT_COUNTRY_LIST.countries]
  }
}

/**
 * jsonConfig 至少含 url；exta 缺省时补 {}，避免详情分支访问 undefined。
 *
 * @param {unknown} raw
 * @param {string} assetType
 * @returns {{ url: string, exta: object }}
 */
function normalizeJsonConfig(raw, assetType) {
  const source = raw && typeof raw === 'object' ? raw : {}
  const url = requireNonEmptyString(source.url, 'jsonConfig.url')
  let exta = source.exta
  if (exta == null || exta === '') {
    exta = {}
  }
  if (typeof exta === 'string') {
    try {
      exta = JSON.parse(exta)
    } catch (e) {
      throw new Error(`资源类型 ${assetType} 的 jsonConfig.exta 不是合法 JSON`)
    }
  }
  if (typeof exta !== 'object' || exta === null || Array.isArray(exta)) {
    exta = {}
  }
  return { url, exta }
}

/**
 * 构建通用资源新建 WallpaperCo。
 *
 * @param {Object} input
 * @param {string} input.resourceName
 * @param {string} input.assetType
 * @param {{ url: string, exta?: object }} input.jsonConfig
 * @param {number|string} [input.defaultLockGroupId]
 * @param {Array} [input.dataSetLockGroupList]
 * @param {string|Array} [input.categoryIdList]
 * @param {Object} [input.countryList]
 * @param {Array|string} [input.androidNeglectVersion]
 * @param {Array|string} [input.iosNeglectVersion]
 * @param {number} [input.androidBeginVersion]
 * @param {number} [input.androidOverVersion]
 * @param {number} [input.iosBeginVersion]
 * @param {number} [input.iosOverVersion]
 * @param {Array} [input.appVersionDataSetList]
 * @param {Array|Set|null} [input.tagKeySet]
 * @param {Array} [input.wallpaperUrlList]
 * @param {Array} [input.wallpaperUrlListAndroid]
 * @param {number|string} [input.authorId] 设计师 id；stickers 初始化完成时按它写入作者名
 * @returns {Object} WallpaperCo plain object（wallpaperName / wallpaperType 契约字段）
 */
export function buildGenericResourceWallpaperCoForCreate(input = {}) {
  const resourceName = requireNonEmptyString(input.resourceName, 'resourceName')
  const assetType = requireNonEmptyString(input.assetType, 'assetType')
  const jsonConfig = normalizeJsonConfig(input.jsonConfig, assetType)

  return toWallpaperCo({
    resourceName,
    assetType,
    jsonConfig,
    defaultLockGroupId: input.defaultLockGroupId ?? '',
    dataSetLockGroupList: normalizeJsonArray(input.dataSetLockGroupList),
    categoryIdList: normalizeCategoryIdList(input.categoryIdList),
    countryList: normalizeCountryList(input.countryList),
    androidNeglectVersion: normalizeNeglectVersion(input.androidNeglectVersion),
    iosNeglectVersion: normalizeNeglectVersion(input.iosNeglectVersion),
    androidBeginVersion: input.androidBeginVersion,
    androidOverVersion: input.androidOverVersion,
    iosBeginVersion: input.iosBeginVersion,
    iosOverVersion: input.iosOverVersion,
    appVersionDataSetList: normalizeJsonArray(input.appVersionDataSetList),
    tagKeySet: normalizeTagKeySet(input.tagKeySet),
    wallpaperUrlList: normalizeJsonArray(input.wallpaperUrlList),
    wallpaperUrlListAndroid: normalizeJsonArray(input.wallpaperUrlListAndroid),
    authorId: input.authorId == null || input.authorId === '' ? null : input.authorId
  })
}
